import { useState, useEffect, useCallback } from 'react';
import { useConfirm } from '../../components/ui/ConfirmProvider';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Loader2, Plus, Pencil, Copy, Trash2, Eye, EyeOff, ExternalLink } from 'lucide-react';
import {
  listPosts,
  listCategories,
  deletePost,
  duplicatePost,
  setStatus,
  type BlogPost,
  type BlogCategory,
} from './blogApi';

const MARKETING_URL =
  (import.meta.env.VITE_MARKETING_URL as string | undefined)?.replace(/\/$/, '') ||
  'https://forgedash.uk';

export function BlogList({ basePath = '/admin/blog' }: { basePath?: string } = {}) {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const confirm = useConfirm();
  const [categories, setCategories] = useState<BlogCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [p, c] = await Promise.all([listPosts(), listCategories()]);
      setPosts(p);
      setCategories(c);
    } catch (e) {
      toast.error(`Failed to load posts: ${(e as Error).message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const labelFor = (slug: string) =>
    categories.find((c) => c.slug === slug)?.display_name || slug;

  const onToggleStatus = async (post: BlogPost) => {
    setBusyId(post.id);
    try {
      const next = post.status === 'published' ? 'draft' : 'published';
      const updated = await setStatus(post, next);
      setPosts((prev) => prev.map((p) => (p.id === post.id ? updated : p)));
      toast.success(next === 'published' ? 'Published' : 'Unpublished');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const onDuplicate = async (post: BlogPost) => {
    setBusyId(post.id);
    try {
      const copy = await duplicatePost(post);
      toast.success('Duplicated as draft');
      navigate(`${basePath}/${copy.id}`);
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  const onDelete = async (post: BlogPost) => {
    if (!await confirm({ title: 'Delete post?', message: `"${post.title}" will be removed. This cannot be undone.`, confirmText: 'Delete', isDangerous: true })) return;
    setBusyId(post.id);
    try {
      await deletePost(post.id);
      setPosts((prev) => prev.filter((p) => p.id !== post.id));
      toast.success('Deleted');
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8 flex items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold text-fg">
            Posts
          </h1>
          <p className="mt-1 text-sm text-fg-3">
            {posts.length} post{posts.length === 1 ? '' : 's'}
          </p>
        </div>
        <Link
          to={`${basePath}/new`}
          className="inline-flex h-11 items-center gap-2 rounded-lg bg-gold-fill px-5 font-display font-semibold text-on-gold transition-colors hover:bg-gold-hi"
        >
          <Plus className="h-4 w-4" />
          New post
        </Link>
      </div>

      {posts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-line py-20 text-center">
          <p className="text-fg-3">No posts yet.</p>
          <Link to={`${basePath}/new`} className="mt-3 inline-block font-semibold text-gold hover:underline">
            Write your first post →
          </Link>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-line">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-line bg-panel/50 text-fg-3">
              <tr>
                <th className="px-4 py-3 font-medium">Title</th>
                <th className="px-4 py-3 font-medium">Category</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Published</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {posts.map((post) => (
                <tr key={post.id} className="border-b border-line last:border-0 hover:bg-tint/[0.02]">
                  <td className="px-4 py-3">
                    <Link to={`${basePath}/${post.id}`} className="font-medium text-fg hover:text-gold">
                      {post.title || <span className="italic text-fg-3">Untitled</span>}
                    </Link>
                    <div className="mt-0.5 font-mono text-xs text-fg-3">/{post.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-fg-2">{labelFor(post.category_slug)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                        post.status === 'published'
                          ? 'bg-success/15 text-success'
                          : 'bg-tint/5 text-fg-3'
                      }`}
                    >
                      {post.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-fg-3">
                    {post.published_at
                      ? new Date(post.published_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {post.status === 'published' && (
                        <a
                          href={`${MARKETING_URL}/blog/${post.category_slug}/${post.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          title="View live"
                          className="rounded-md p-2 text-fg-3 hover:bg-tint/5 hover:text-fg"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                      <button
                        onClick={() => onToggleStatus(post)}
                        disabled={busyId === post.id}
                        title={post.status === 'published' ? 'Unpublish' : 'Publish'}
                        className="rounded-md p-2 text-fg-3 hover:bg-tint/5 hover:text-fg disabled:opacity-50"
                      >
                        {post.status === 'published' ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                      <Link
                        to={`${basePath}/${post.id}`}
                        title="Edit"
                        className="rounded-md p-2 text-fg-3 hover:bg-tint/5 hover:text-fg"
                      >
                        <Pencil className="h-4 w-4" />
                      </Link>
                      <button
                        onClick={() => onDuplicate(post)}
                        disabled={busyId === post.id}
                        title="Duplicate"
                        className="rounded-md p-2 text-fg-3 hover:bg-tint/5 hover:text-fg disabled:opacity-50"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => onDelete(post)}
                        disabled={busyId === post.id}
                        title="Delete"
                        className="rounded-md p-2 text-fg-3 hover:bg-danger/10 hover:text-danger disabled:opacity-50"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
